// hooks/useIPFSMetadata.js
import { useState, useCallback } from 'react';
import { uploadToPinata } from '../lib/ipfs'; // Import your chosen IPFS service

const useIPFSMetadata = () => {
    const [isUploadingMetadata, setIsUploadingMetadata] = useState(false);
    const [metadataError, setMetadataError] = useState(null);

    // Helper function to upload file to IPFS
    const uploadFileToIPFS = async (file) => {
        try {
            const ipfsHash = await uploadToPinata.uploadFile(file);
            return ipfsHash;
        } catch (error) {
            throw new Error(`Failed to upload file to IPFS: ${error.message}`);
        }
    };

    // Helper function to upload JSON metadata to IPFS
    const uploadJSONToIPFS = async (jsonData) => {
        try {
            const ipfsHash = await uploadToPinata.uploadJSON(jsonData);
            return ipfsHash;
        } catch (error) {
            throw new Error(`Failed to upload JSON to IPFS: ${error.message}`);
        }
    };

    // Main function to generate IPFS metadata
    const generateIPFSMetadata = useCallback(async (formData) => {
        setIsUploadingMetadata(true);
        setMetadataError(null);

        try {
            const metadata = {};

            // Helper function to check if field exists and has value
            const addFieldIfExists = (key, value) => {
                if (value && value !== '' && value !== null && value !== undefined) {
                    metadata[key] = value;
                }
            };

            // Upload logo to IPFS if it exists
            if (formData.logo) {
                try {
                    const logoHash = await uploadFileToIPFS(formData.logo);
                    metadata.logo = `ipfs://${logoHash}`;
                } catch (error) {
                    console.error('Failed to upload logo to IPFS:', error);
                    // Continue without logo if upload fails
                }
            }

            // Add other fields only if they exist
            addFieldIfExists('chain', formData.chain);
            addFieldIfExists('projectName', formData.projectName);
            addFieldIfExists('tagline', formData.tagline);
            addFieldIfExists('poolDescription', formData.poolDescription);
            addFieldIfExists('website', formData.website);
            addFieldIfExists('twitter', formData.twitter);
            addFieldIfExists('instagram', formData.instagram);
            addFieldIfExists('discord', formData.discord);
            addFieldIfExists('profile', formData.profile)
            // Add timestamp
            metadata.createdAt = new Date().toISOString();

            // Only upload to IPFS if we have metadata to upload
            if (Object.keys(metadata).length === 0) {
                throw new Error('No metadata fields to upload');
            }

            // Upload metadata JSON to IPFS
            const metadataHash = await uploadJSONToIPFS(metadata);
            const ipfsLink = `ipfs://${metadataHash}`;

            setIsUploadingMetadata(false);
            return {
                success: true,
                ipfsLink,
                metadata,
            };

        } catch (error) {
            console.error('Error generating IPFS metadata:', error);
            setMetadataError(error.message);
            setIsUploadingMetadata(false);
            return {
                success: false,
                error: error.message,
            };
        }
    }, []);

    return {
        generateIPFSMetadata,
        isUploadingMetadata,
        metadataError,
    };
};

export default useIPFSMetadata;